import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useStore';
import { social as socialApi } from '../lib/api';
import toast from 'react-hot-toast';

interface User {
  id: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  bio: string | null;
  _count?: {
    shares: number;
    comments: number;
  };
}

export default function Users() {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'activity' | 'recent' | 'name'>('activity');

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const allUsers = await socialApi.getAllUsers(token);
      setUsers(allUsers);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = users.filter(user =>
        user.username.toLowerCase().includes(query) ||
        user.displayName?.toLowerCase().includes(query) ||
        user.bio?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'activity') {
        const aActivity = (a._count?.shares || 0) + (a._count?.comments || 0);
        const bActivity = (b._count?.shares || 0) + (b._count?.comments || 0);
        return bActivity - aActivity; // Descending
      } else if (sortBy === 'name') {
        const aName = a.displayName || a.username;
        const bName = b.displayName || b.username;
        return aName.localeCompare(bName);
      } else { // recent - would need createdAt field, for now use activity
        return 0; // Keep original order
      }
    });

    return sorted;
  }, [users, searchQuery, sortBy]);

  function handleUserClick(userId: string) {
    navigate(`/user/${userId}`);
  }

  if (loading) {
    return (
      <div className="pl-4 lg:pl-6 py-10">
        <span className="kicker">Loading…</span>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-canvas page-transition">
      {/* Header */}
      <div className="pl-4 lg:pl-6 pr-4 pt-8 pb-5">
        <div className="kicker mb-1">Users · {users.length}</div>
        <h1 className="text-3xl">Readers on this instance.</h1>
      </div>

      {/* Search + sort as a shared-border toolbar */}
      {users.length > 0 && (
        <div className="sticky top-0 z-10 bg-canvas flex items-stretch border-y border-edge">
          <input
            type="text"
            placeholder="Search by name or bio…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="field flex-1 min-w-0 border-0 border-r border-edge pl-4 lg:pl-6 h-10 py-0"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'activity' | 'recent' | 'name')}
            className="field-mono font-mono text-[11px] tracking-[0.08em] uppercase text-ink-secondary bg-canvas h-10 px-3 border-0 appearance-none cursor-pointer hover:bg-surface-sunken"
          >
            <option value="activity">Most active</option>
            <option value="name">Name A–Z</option>
          </select>
        </div>
      )}

      {/* Empty */}
      {users.length === 0 && (
        <div className="pl-4 lg:pl-6 pr-4 py-10 max-w-list border-t border-edge">
          <div className="kicker mb-1">Empty</div>
          <p className="text-ink-secondary">You're the only reader on this instance right now.</p>
        </div>
      )}

      {users.length > 0 && filteredAndSortedUsers.length === 0 && (
        <div className="pl-4 lg:pl-6 pr-4 py-10 max-w-list">
          <div className="kicker mb-1">No match</div>
          <p className="text-ink-secondary">Nobody matches that search.</p>
        </div>
      )}

      {/* Rows */}
      {filteredAndSortedUsers.length > 0 && (
        <div>
          {filteredAndSortedUsers.map((user, index) => {
            const shares = user._count?.shares || 0;
            const comments = user._count?.comments || 0;
            return (
              <div
                key={user.id}
                role="button"
                tabIndex={0}
                onClick={() => handleUserClick(user.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleUserClick(user.id); }}
                className="grid grid-cols-[3.5rem_1fr] lg:grid-cols-[4.5rem_1fr] border-b border-edge-subtle cursor-pointer hover:bg-surface-sunken"
              >
                <div className="flex items-start pl-2 lg:pl-3 pr-2 py-3 border-r border-edge-subtle">
                  <span className="stamp">№ {String(index + 1).padStart(3, '0')}</span>
                </div>
                <div className="min-w-0 flex items-center gap-4 pl-3 lg:pl-4 pr-4 py-3 max-w-list">
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.username}
                      className="w-10 h-10 rounded-full object-cover avatar-duotone flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-ink text-paper flex items-center justify-center font-display font-extrabold flex-shrink-0">
                      {(user.displayName?.[0] || user.username[0]).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="kicker">
                      @{user.username}
                      {shares > 0 && <> · {shares} {shares === 1 ? 'share' : 'shares'}</>}
                      {comments > 0 && <> · {comments} {comments === 1 ? 'comment' : 'comments'}</>}
                    </div>
                    <div className="text-[15px] font-semibold text-ink leading-snug truncate">
                      {user.displayName || user.username}
                    </div>
                    {user.bio && (
                      <div className="text-sm text-ink-secondary truncate">{user.bio}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
